import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@tabipla/ui/components/dialog";
import { Button } from "@tabipla/ui/components/button";
import { Input } from "@tabipla/ui/components/input";
import { Copy, Check, Link2, Link2Off, Loader2, ExternalLink } from "lucide-react";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareCode: string | null;
  onShare: () => Promise<void>;
  onUnshare: () => Promise<void>;
  isSharing: boolean;
}

export function ShareDialog({
  open,
  onOpenChange,
  shareCode,
  onShare,
  onUnshare,
  isSharing,
}: ShareDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const shareUrl = shareCode ? `${window.location.origin}/booklet/${shareCode}` : "";

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    await onShare();
  };

  const handleUnshare = async () => {
    await onUnshare();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("share.title")}</DialogTitle>
          <DialogDescription>{t("share.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {shareCode ? (
            <>
              <div className="flex gap-2">
                <Input value={shareUrl} readOnly className="flex-1" />
                <Button variant="outline" size="icon" onClick={handleCopy}>
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" asChild>
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t("share.viewBooklet")}
                  </a>
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleUnshare}
                  disabled={isSharing}
                  className="flex-1"
                >
                  {isSharing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Link2Off className="mr-2 h-4 w-4" />
                  )}
                  {t("share.stopSharing")}
                </Button>
              </div>
            </>
          ) : (
            <Button onClick={handleShare} disabled={isSharing} className="w-full">
              {isSharing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("share.generating")}
                </>
              ) : (
                <>
                  <Link2 className="mr-2 h-4 w-4" />
                  {t("share.generateLink")}
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
